import { fabric } from 'fabric';
import { useCallback } from 'react';
import Vec2 from 'vec2';

import {
  extractApplianceFromGroupToCanvas,
  findShapeByGroupTargetName,
  isApplianceName,
  lockShapeMovement,
  movePreviousApplianceBackToGroup,
  selectCanvasRootAppliance,
  setupApplianceInteractionRefs,
} from './useMouseDownBefore.utils';

import {
  getOutlinePointsFromShape,
  isPointInsidePolygon,
} from '~/core/helpers';
import { findApplianceInShapes } from '~/core/hooks/useAppliance';
import {
  useDrawingShapeContext,
  useFabricCanvasContext,
} from '~/legacy/easy-estimator-page/context';
import { findAppliedApplianceName } from '~/legacy/easy-estimator-page/context/drawingShapeContext/drawingShape.utils';
import { useAppDispatch } from '~/redux/hooks';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { updateSelectedDrawingObjectName } from '~/redux/slices/shape';

export const useMouseDownBefore = (canvas: fabric.Canvas) => {
  const dispatch = useAppDispatch();

  const {
    getters: { activeDrawingApplianceId },
    handlers: { updateActiveDrawingAppliance, updateActiveDrawingShapeId },
  } = useDrawingShapeContext();

  const {
    drawingShapesRef,
    isShapeLogicRef,
    movingItemRef,
    shapeStartDragRef,
  } = useFabricCanvasContext();

  const handleSelectionCircle = useCallback(
    (pointer: Vec2) => {
      const shape = drawingShapesRef.current.find((shape) =>
        isPointInsidePolygon(getOutlinePointsFromShape(shape), pointer)
      );

      if (shape) {
        updateActiveDrawingShapeId(shape.id);
        dispatch(updateNeedRedrawCanvas(true));
      }
    },
    [dispatch, drawingShapesRef, updateActiveDrawingShapeId]
  );

  const handleCanvasRootAppliance = useCallback(
    (target: fabric.Object, pointer: Vec2) => {
      if (!target.name) {
        return false;
      }

      const found = findApplianceInShapes(
        target.name,
        drawingShapesRef.current
      );

      if (!found?.appliance) {
        return false;
      }

      dispatch(
        updateSelectedDrawingObjectName(
          findAppliedApplianceName(target.name, drawingShapesRef.current)
        )
      );
      setupApplianceInteractionRefs({
        isShapeLogicRef,
        movingItemRef,
        shapeStartDragRef,
        applianceId: target.name,
        pointer,
      });
      selectCanvasRootAppliance(canvas, target);

      return true;
    },
    [
      canvas,
      dispatch,
      drawingShapesRef,
      isShapeLogicRef,
      movingItemRef,
      shapeStartDragRef,
    ]
  );

  const handleNonGroupMemberClick = useCallback(
    (target: fabric.Object) => {
      if (!target.name) {
        return;
      }

      const displayName = findAppliedApplianceName(
        target.name,
        drawingShapesRef.current
      );

      // Guard on a non-empty result: this branch receives non-appliance
      // targets, and dispatching '' would clear selectedDrawingObjectName.
      if (displayName) {
        dispatch(updateSelectedDrawingObjectName(displayName));
      }
    },
    [dispatch, drawingShapesRef]
  );

  const handleGroupApplianceClick = useCallback(
    (
      target: fabric.Object,
      groupMemberClicked: fabric.Object,
      targetName: string | undefined,
      pointer: Vec2
    ) => {
      const shape = findShapeByGroupTargetName(
        drawingShapesRef.current,
        targetName
      );

      if (
        !shape ||
        !groupMemberClicked.name ||
        !isApplianceName(groupMemberClicked.name)
      ) {
        return;
      }

      const applianceId = groupMemberClicked.name;
      const applianceName = findAppliedApplianceName(
        applianceId,
        drawingShapesRef.current
      );

      movePreviousApplianceBackToGroup({
        canvas,
        activeDrawingApplianceId,
        newApplianceId: applianceId,
        drawingShapes: drawingShapesRef.current,
      });

      lockShapeMovement(target);
      setupApplianceInteractionRefs({
        isShapeLogicRef,
        movingItemRef,
        shapeStartDragRef,
        applianceId,
        pointer,
      });
      extractApplianceFromGroupToCanvas(canvas, shape, groupMemberClicked);

      updateActiveDrawingAppliance(applianceId);
      dispatch(updateSelectedDrawingObjectName(applianceName));
      canvas.renderAll();
    },
    [
      canvas,
      dispatch,
      drawingShapesRef,
      isShapeLogicRef,
      movingItemRef,
      shapeStartDragRef,
      updateActiveDrawingAppliance,
      activeDrawingApplianceId,
    ]
  );

  const handleApplianceInteraction = useCallback(
    (
      target: fabric.Object,
      groupMemberClicked: fabric.Object | undefined,
      targetName: string | undefined,
      pointer: Vec2
    ) => {
      isShapeLogicRef.current = true;

      if (!groupMemberClicked?.name) {
        // Handle appliance directly on canvas root (after drop)
        if (isApplianceName(target.name)) {
          handleCanvasRootAppliance(target, pointer);

          return;
        }

        // Handle a non-group, non-appliance-named target click
        handleNonGroupMemberClick(target);

        return;
      }

      // Handle appliance clicked within a shape group
      handleGroupApplianceClick(
        target,
        groupMemberClicked,
        targetName,
        pointer
      );
    },
    [
      isShapeLogicRef,
      handleCanvasRootAppliance,
      handleNonGroupMemberClick,
      handleGroupApplianceClick,
    ]
  );

  return {
    handleSelectionCircle,
    handleApplianceInteraction,
  };
};
